import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useStore } from '@/contexts/StoreContext';
import { dashboardAPI, levelsAPI, rewardsAPI, promotionsAPI, usersAPI, rulesAPI, brandingAPI } from '@/lib/api';
import { formatPoints, formatNumber, formatDate, theme } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  LayoutDashboard,
  Palette,
  Gift,
  Trophy,
  Megaphone,
  Settings,
  Users,
  Store,
  LogOut,
  Sun,
  Moon,
  TrendingUp,
  Eye,
  Plus,
  Pencil,
  Trash2,
  Save,
  Loader2,
  ChevronRight,
  BarChart3
} from 'lucide-react';

// Store Selector for Back Office
function BackOfficeSelector({ onSelect }) {
  const { stores, loading } = useStore();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-indigo-600/20 flex items-center justify-center mx-auto mb-4">
            <Store className="w-8 h-8 text-indigo-400" />
          </div>
          <h1 className="font-outfit text-3xl font-bold text-foreground">Back Office</h1>
          <p className="text-muted-foreground mt-2">Selecciona la tienda a administrar</p>
        </div>
        
        <div className="space-y-4">
          {stores.map((store) => (
            <Card
              key={store.id}
              className="glass border-white/10 cursor-pointer card-hover"
              onClick={() => onSelect(store)}
              data-testid={`store-admin-${store.id}`}
            >
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-indigo-600/20 flex items-center justify-center">
                  <Store className="w-7 h-7 text-indigo-400" />
                </div>
                <div className="flex-1">
                  <h3 className="font-outfit text-lg font-semibold text-foreground">{store.name}</h3>
                  <p className="text-muted-foreground">{store.description || 'Programa de fidelización'}</p>
                </div>
                <ChevronRight className="w-6 h-6 text-muted-foreground" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

// Sidebar Navigation
function Sidebar({ activeTab, setActiveTab }) {
  const { user, logout } = useAuth();
  const { currentStore, clearStore } = useStore();
  const navigate = useNavigate();
  const [isDark, setIsDark] = useState(theme.get() === 'dark');

  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'branding', label: 'Branding', icon: Palette },
    { id: 'levels', label: 'Niveles', icon: Trophy },
    { id: 'rewards', label: 'Premios', icon: Gift },
    { id: 'promotions', label: 'Promociones', icon: Megaphone },
    { id: 'users', label: 'Usuarios', icon: Users },
    { id: 'settings', label: 'Configuración', icon: Settings },
  ];

  const toggleTheme = () => {
    const newTheme = theme.toggle();
    setIsDark(newTheme === 'dark');
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <aside className="w-64 h-screen glass border-r border-white/10 flex flex-col">
      {/* Header */}
      <div className="p-6 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-600/20 flex items-center justify-center">
            <Store className="w-5 h-5 text-indigo-400" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-outfit font-bold text-foreground truncate">{currentStore?.name}</h2>
            <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1">
        {navItems.map((item) => (
          <button
            key={item.id}
            onClick={() => setActiveTab(item.id)}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-colors ${
              activeTab === item.id
                ? 'bg-indigo-600/20 text-indigo-400'
                : 'text-muted-foreground hover:bg-white/5 hover:text-foreground'
            }`}
            data-testid={`nav-${item.id}`}
          >
            <item.icon className="w-5 h-5" />
            <span className="font-medium">{item.label}</span>
          </button>
        ))}
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-white/10 space-y-2">
        <Button
          variant="ghost"
          className="w-full justify-start text-muted-foreground"
          onClick={toggleTheme}
        >
          {isDark ? <Sun className="w-5 h-5 mr-3" /> : <Moon className="w-5 h-5 mr-3" />}
          {isDark ? 'Modo Claro' : 'Modo Oscuro'}
        </Button>
        <Button
          variant="ghost"
          className="w-full justify-start text-muted-foreground"
          onClick={clearStore}
        >
          <Store className="w-5 h-5 mr-3" />
          Cambiar Tienda
        </Button>
        <Button
          variant="ghost"
          className="w-full justify-start text-muted-foreground"
          onClick={handleLogout}
        >
          <LogOut className="w-5 h-5 mr-3" />
          Cerrar Sesión
        </Button>
      </div>
    </aside>
  );
}

// Dashboard Tab
function DashboardTab() {
  const { currentStore } = useStore();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, [currentStore]);

  const loadStats = async () => {
    try {
      const response = await dashboardAPI.getStats(currentStore.id);
      setStats(response.data);
    } catch (err) {
      console.error('Error loading stats:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-outfit text-2xl font-bold text-foreground">Dashboard</h1>
        <p className="text-muted-foreground">Resumen de tu programa de fidelización</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="glass border-white/10">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Clientes</p>
                <p className="font-outfit text-3xl font-bold text-foreground">{formatNumber(stats?.total_customers || 0)}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-indigo-600/20 flex items-center justify-center">
                <Users className="w-6 h-6 text-indigo-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass border-white/10">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Visitas Totales</p>
                <p className="font-outfit text-3xl font-bold text-foreground">{formatNumber(stats?.total_visits || 0)}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-green-600/20 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass border-white/10">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Visitas Hoy</p>
                <p className="font-outfit text-3xl font-bold text-foreground">{formatNumber(stats?.today_visits || 0)}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-amber-600/20 flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-amber-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass border-white/10">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Canjes</p>
                <p className="font-outfit text-3xl font-bold text-foreground">{formatNumber(stats?.total_redemptions || 0)}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-purple-600/20 flex items-center justify-center">
                <Gift className="w-6 h-6 text-purple-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Visits Chart Placeholder */}
      <Card className="glass border-white/10">
        <CardHeader>
          <CardTitle className="font-outfit text-foreground">Visitas Últimos 7 Días</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64 flex items-end justify-around gap-2">
            {Object.entries(stats?.visits_by_day || {}).slice(-7).map(([day, count]) => (
              <div key={day} className="flex flex-col items-center gap-2">
                <div
                  className="w-12 bg-indigo-600/50 rounded-t-lg transition-all"
                  style={{ height: `${Math.max(20, (count / Math.max(...Object.values(stats?.visits_by_day || {1: 1}))) * 200)}px` }}
                />
                <span className="text-xs text-muted-foreground">{day.slice(5)}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Branding Tab
function BrandingTab() {
  const { currentStore, branding, updateBranding } = useStore();
  const [formData, setFormData] = useState({
    logo_url: '',
    primary_color: '#6366F1',
    secondary_color: '#8B5CF6',
    accent_color: '#F59E0B',
    background_color: '#0B0C15',
    text_color: '#FAFAFA',
    hero_image_url: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (branding) {
      setFormData({
        logo_url: branding.logo_url || '',
        primary_color: branding.primary_color || '#6366F1',
        secondary_color: branding.secondary_color || '#8B5CF6',
        accent_color: branding.accent_color || '#F59E0B',
        background_color: branding.background_color || '#0B0C15',
        text_color: branding.text_color || '#FAFAFA',
        hero_image_url: branding.hero_image_url || '',
      });
    }
  }, [branding]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateBranding(currentStore.id, formData);
      toast.success('Branding actualizado');
    } catch (err) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-outfit text-2xl font-bold text-foreground">Branding</h1>
        <p className="text-muted-foreground">Personaliza la apariencia de tu programa</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Form */}
        <Card className="glass border-white/10">
          <CardHeader>
            <CardTitle className="font-outfit text-foreground">Configuración Visual</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-foreground">URL del Logo</Label>
              <Input
                value={formData.logo_url}
                onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                placeholder="https://..."
                className="bg-white/5 border-white/10 text-foreground"
                data-testid="branding-logo-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-foreground">Color Primario</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={formData.primary_color}
                    onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                    className="w-12 h-10 p-1 bg-transparent border-white/10"
                  />
                  <Input
                    value={formData.primary_color}
                    onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                    className="bg-white/5 border-white/10 text-foreground"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-foreground">Color Secundario</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={formData.secondary_color}
                    onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                    className="w-12 h-10 p-1 bg-transparent border-white/10"
                  />
                  <Input
                    value={formData.secondary_color}
                    onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                    className="bg-white/5 border-white/10 text-foreground"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-foreground">Color de Acento</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={formData.accent_color}
                    onChange={(e) => setFormData({ ...formData, accent_color: e.target.value })}
                    className="w-12 h-10 p-1 bg-transparent border-white/10"
                  />
                  <Input
                    value={formData.accent_color}
                    onChange={(e) => setFormData({ ...formData, accent_color: e.target.value })}
                    className="bg-white/5 border-white/10 text-foreground"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-foreground">Color de Fondo</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={formData.background_color}
                    onChange={(e) => setFormData({ ...formData, background_color: e.target.value })}
                    className="w-12 h-10 p-1 bg-transparent border-white/10"
                  />
                  <Input
                    value={formData.background_color}
                    onChange={(e) => setFormData({ ...formData, background_color: e.target.value })}
                    className="bg-white/5 border-white/10 text-foreground"
                  />
                </div>
              </div>
            </div>

            <Button onClick={handleSave} disabled={saving} className="w-full bg-indigo-600 hover:bg-indigo-500" data-testid="save-branding-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Guardar Cambios
            </Button>
          </CardContent>
        </Card>

        {/* Preview */}
        <Card className="glass border-white/10">
          <CardHeader>
            <CardTitle className="font-outfit text-foreground flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Vista Previa
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div
              className="rounded-xl p-6 space-y-4"
              style={{ backgroundColor: formData.background_color, color: formData.text_color }}
            >
              <div className="flex items-center gap-3">
                {formData.logo_url ? (
                  <img src={formData.logo_url} alt="Logo" className="w-12 h-12 rounded-xl object-cover" />
                ) : (
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: formData.primary_color + '33' }}>
                    <Gift className="w-6 h-6" style={{ color: formData.primary_color }} />
                  </div>
                )}
                <h3 className="font-outfit font-bold">{currentStore?.name}</h3>
              </div>

              <div className="rounded-lg p-4" style={{ backgroundColor: formData.primary_color + '22' }}>
                <p className="text-sm opacity-70">Tus puntos</p>
                <p className="font-outfit text-2xl font-bold">1,250 pts</p>
              </div>

              <button
                className="w-full py-3 rounded-full font-medium"
                style={{ backgroundColor: formData.primary_color, color: '#fff' }}
              >
                Mostrar mi QR
              </button>

              <div className="flex gap-2">
                <span className="px-3 py-1 rounded-full text-sm" style={{ backgroundColor: formData.accent_color + '33', color: formData.accent_color }}>
                  Nivel Gold
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Levels Tab
function LevelsTab() {
  const { currentStore } = useStore();
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLevel, setEditingLevel] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    min_points: 0,
    order: 1,
    color: '#6366F1',
  });

  useEffect(() => {
    loadLevels();
  }, [currentStore]);

  const loadLevels = async () => {
    try {
      const response = await levelsAPI.list(currentStore.id);
      setLevels(response.data);
    } catch (err) {
      console.error('Error loading levels:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setEditingLevel(null);
    setFormData({
      name: '',
      description: '',
      min_points: 0,
      order: levels.length + 1,
      color: '#6366F1',
    });
    setDialogOpen(true);
  };

  const handleOpenEdit = (level) => {
    setEditingLevel(level);
    setFormData({
      name: level.name,
      description: level.description || '',
      min_points: level.min_points,
      order: level.order,
      color: level.color || '#6366F1',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingLevel) {
        await levelsAPI.update(currentStore.id, editingLevel.id, formData);
        toast.success('Nivel actualizado');
      } else {
        await levelsAPI.create(currentStore.id, formData);
        toast.success('Nivel creado');
      }
      loadLevels();
      setDialogOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (level) => {
    if (!confirm('¿Eliminar este nivel?')) return;
    try {
      await levelsAPI.delete(currentStore.id, level.id);
      toast.success('Nivel eliminado');
      loadLevels();
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-outfit text-2xl font-bold text-foreground">Niveles</h1>
          <p className="text-muted-foreground">Configura los niveles de tu programa</p>
        </div>
        <Button onClick={handleOpenCreate} className="bg-indigo-600 hover:bg-indigo-500" data-testid="add-level-btn">
          <Plus className="w-4 h-4 mr-2" />
          Agregar Nivel
        </Button>
      </div>

      {levels.length === 0 ? (
        <Card className="glass border-white/10">
          <CardContent className="p-8 text-center">
            <Trophy className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">No hay niveles configurados</p>
            <Button onClick={handleOpenCreate} className="mt-4 bg-indigo-600 hover:bg-indigo-500">
              Crear primer nivel
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {levels.map((level) => (
            <Card key={level.id} className="glass border-white/10" data-testid={`level-card-${level.id}`}>
              <CardContent className="p-4 flex items-center gap-4">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: (level.color || '#6366F1') + '33' }}
                >
                  <Trophy className="w-6 h-6" style={{ color: level.color || '#6366F1' }} />
                </div>
                <div className="flex-1">
                  <h3 className="font-outfit font-semibold text-foreground">{level.name}</h3>
                  <p className="text-sm text-muted-foreground">
                    {formatPoints(level.min_points)} mínimo • Orden: {level.order}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="icon" onClick={() => handleOpenEdit(level)} data-testid={`edit-level-${level.id}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(level)} className="text-red-400" data-testid={`delete-level-${level.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="glass-panel border-white/10">
          <DialogHeader>
            <DialogTitle className="font-outfit text-foreground">{editingLevel ? 'Editar' : 'Crear'} Nivel</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-foreground">Nombre</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ej: Bronze, Silver, Gold..."
                className="bg-white/5 border-white/10 text-foreground"
                data-testid="level-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-foreground">Descripción</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descripción opcional..."
                className="bg-white/5 border-white/10 text-foreground"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-foreground">Puntos Mínimos</Label>
                <Input
                  type="number"
                  value={formData.min_points}
                  onChange={(e) => setFormData({ ...formData, min_points: parseInt(e.target.value) || 0 })}
                  className="bg-white/5 border-white/10 text-foreground"
                  data-testid="level-points-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-foreground">Orden</Label>
                <Input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 1 })}
                  className="bg-white/5 border-white/10 text-foreground"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-foreground">Color</Label>
              <div className="flex gap-2">
                <Input
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="w-12 h-10 p-1 bg-transparent border-white/10"
                />
                <Input
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="bg-white/5 border-white/10 text-foreground"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-white/10 text-foreground">
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-500" data-testid="save-level-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Rewards Tab
function RewardsTab() {
  const { currentStore } = useStore();
  const [rewards, setRewards] = useState([]);
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingReward, setEditingReward] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    level_id: '',
    points_cost: 100,
    expiration_days: 30,
    image_url: '',
  });

  useEffect(() => {
    loadData();
  }, [currentStore]);

  const loadData = async () => {
    try {
      const [rewardsRes, levelsRes] = await Promise.all([
        rewardsAPI.list(currentStore.id),
        levelsAPI.list(currentStore.id),
      ]);
      setRewards(rewardsRes.data);
      setLevels(levelsRes.data);
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setEditingReward(null);
    setFormData({
      name: '',
      description: '',
      level_id: levels[0]?.id || '',
      points_cost: 100,
      expiration_days: 30,
      image_url: '',
    });
    setDialogOpen(true);
  };

  const handleOpenEdit = (reward) => {
    setEditingReward(reward);
    setFormData({
      name: reward.name,
      description: reward.description || '',
      level_id: reward.level_id,
      points_cost: reward.points_cost,
      expiration_days: reward.expiration_days,
      image_url: reward.image_url || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingReward) {
        await rewardsAPI.update(currentStore.id, editingReward.id, formData);
        toast.success('Premio actualizado');
      } else {
        await rewardsAPI.create(currentStore.id, formData);
        toast.success('Premio creado');
      }
      loadData();
      setDialogOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (reward) => {
    if (!confirm('¿Eliminar este premio?')) return;
    try {
      await rewardsAPI.delete(currentStore.id, reward.id);
      toast.success('Premio eliminado');
      loadData();
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };

  const getLevelName = (levelId) => {
    const level = levels.find(l => l.id === levelId);
    return level?.name || 'Sin nivel';
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-outfit text-2xl font-bold text-foreground">Premios</h1>
          <p className="text-muted-foreground">Gestiona los premios canjeables</p>
        </div>
        <Button onClick={handleOpenCreate} className="bg-indigo-600 hover:bg-indigo-500" disabled={levels.length === 0} data-testid="add-reward-btn">
          <Plus className="w-4 h-4 mr-2" />
          Agregar Premio
        </Button>
      </div>

      {levels.length === 0 ? (
        <Card className="glass border-white/10">
          <CardContent className="p-8 text-center">
            <Trophy className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">Primero debes crear niveles para agregar premios</p>
          </CardContent>
        </Card>
      ) : rewards.length === 0 ? (
        <Card className="glass border-white/10">
          <CardContent className="p-8 text-center">
            <Gift className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">No hay premios configurados</p>
            <Button onClick={handleOpenCreate} className="mt-4 bg-indigo-600 hover:bg-indigo-500">
              Crear primer premio
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {rewards.map((reward) => (
            <Card key={reward.id} className="glass border-white/10" data-testid={`reward-card-${reward.id}`}>
              {reward.image_url && (
                <img src={reward.image_url} alt={reward.name} className="w-full h-32 object-cover rounded-t-lg" />
              )}
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-outfit font-semibold text-foreground">{reward.name}</h3>
                  <Badge variant="outline" className="border-indigo-400/50 text-indigo-400">
                    {getLevelName(reward.level_id)}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground mb-3">{reward.description}</p>
                <div className="flex items-center justify-between">
                  <span className="font-outfit font-bold text-amber-400">{formatPoints(reward.points_cost)}</span>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => handleOpenEdit(reward)}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(reward)} className="text-red-400">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="glass-panel border-white/10">
          <DialogHeader>
            <DialogTitle className="font-outfit text-foreground">{editingReward ? 'Editar' : 'Crear'} Premio</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-foreground">Nombre</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ej: Café gratis, 10% descuento..."
                className="bg-white/5 border-white/10 text-foreground"
                data-testid="reward-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-foreground">Descripción</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="bg-white/5 border-white/10 text-foreground"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-foreground">Nivel Requerido</Label>
              <Select value={formData.level_id} onValueChange={(v) => setFormData({ ...formData, level_id: v })}>
                <SelectTrigger className="bg-white/5 border-white/10 text-foreground">
                  <SelectValue placeholder="Seleccionar nivel" />
                </SelectTrigger>
                <SelectContent>
                  {levels.map((level) => (
                    <SelectItem key={level.id} value={level.id}>{level.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-foreground">Costo en Puntos</Label>
                <Input
                  type="number"
                  value={formData.points_cost}
                  onChange={(e) => setFormData({ ...formData, points_cost: parseInt(e.target.value) || 0 })}
                  className="bg-white/5 border-white/10 text-foreground"
                  data-testid="reward-points-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-foreground">Días de Expiración</Label>
                <Input
                  type="number"
                  value={formData.expiration_days}
                  onChange={(e) => setFormData({ ...formData, expiration_days: parseInt(e.target.value) || 30 })}
                  className="bg-white/5 border-white/10 text-foreground"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-foreground">URL de Imagen (opcional)</Label>
              <Input
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                placeholder="https://..."
                className="bg-white/5 border-white/10 text-foreground"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-white/10 text-foreground">
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-500" data-testid="save-reward-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Promotions Tab - Simplified for brevity
function PromotionsTab() {
  const { currentStore } = useStore();
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPromotions();
  }, [currentStore]);

  const loadPromotions = async () => {
    try {
      const response = await promotionsAPI.listAll(currentStore.id);
      setPromotions(response.data);
    } catch (err) {
      console.error('Error loading promotions:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-outfit text-2xl font-bold text-foreground">Promociones</h1>
          <p className="text-muted-foreground">Gestiona las promociones de tu tienda</p>
        </div>
        <Button className="bg-indigo-600 hover:bg-indigo-500" data-testid="add-promotion-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nueva Promoción
        </Button>
      </div>

      {promotions.length === 0 ? (
        <Card className="glass border-white/10">
          <CardContent className="p-8 text-center">
            <Megaphone className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">No hay promociones configuradas</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {promotions.map((promo) => (
            <Card key={promo.id} className="glass border-white/10">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-16 h-16 rounded-xl bg-purple-600/20 flex items-center justify-center">
                  <Megaphone className="w-8 h-8 text-purple-400" />
                </div>
                <div className="flex-1">
                  <h3 className="font-outfit font-semibold text-foreground">{promo.title}</h3>
                  <p className="text-sm text-muted-foreground">{promo.description}</p>
                  <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                    <span>Desde: {formatDate(promo.start_date)}</span>
                    <span>Hasta: {formatDate(promo.end_date)}</span>
                  </div>
                </div>
                <Badge className={promo.is_active ? 'bg-green-600/20 text-green-400' : 'bg-gray-600/20 text-gray-400'}>
                  {promo.is_active ? 'Activa' : 'Inactiva'}
                </Badge>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// Users Tab
function UsersTab() {
  const { currentStore } = useStore();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStaff();
  }, [currentStore]);

  const loadStaff = async () => {
    try {
      const response = await usersAPI.listStaff(currentStore.id);
      setStaff(response.data);
    } catch (err) {
      console.error('Error loading staff:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-outfit text-2xl font-bold text-foreground">Usuarios</h1>
          <p className="text-muted-foreground">Gestiona el equipo de tu tienda</p>
        </div>
        <Button className="bg-indigo-600 hover:bg-indigo-500" data-testid="add-user-btn">
          <Plus className="w-4 h-4 mr-2" />
          Agregar Usuario
        </Button>
      </div>

      <div className="space-y-3">
        {staff.map((user) => (
          <Card key={user.id} className="glass border-white/10">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-indigo-600/20 flex items-center justify-center font-outfit font-bold text-indigo-400">
                {user.full_name?.charAt(0) || '?'}
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-foreground">{user.full_name}</h3>
                <p className="text-sm text-muted-foreground">{user.email}</p>
              </div>
              <Badge variant="outline" className="border-indigo-400/50 text-indigo-400">
                {user.role === 'store_admin' ? 'Admin' : 'Staff'}
              </Badge>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

// Settings Tab
function SettingsTab() {
  const { currentStore } = useStore();
  const [rules, setRules] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    points_per_visit: 10,
    max_visits_per_day: 1,
    visit_cooldown_hours: 4,
  });

  useEffect(() => {
    loadRules();
  }, [currentStore]);

  const loadRules = async () => {
    try {
      const response = await rulesAPI.get(currentStore.id);
      setRules(response.data);
      setFormData({
        points_per_visit: response.data.points_per_visit || 10,
        max_visits_per_day: response.data.max_visits_per_day || 1,
        visit_cooldown_hours: response.data.visit_cooldown_hours || 4,
      });
    } catch (err) {
      console.error('Error loading rules:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await rulesAPI.update(currentStore.id, formData);
      toast.success('Configuración guardada');
    } catch (err) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-outfit text-2xl font-bold text-foreground">Configuración</h1>
        <p className="text-muted-foreground">Reglas del programa de fidelización</p>
      </div>

      <Card className="glass border-white/10">
        <CardHeader>
          <CardTitle className="font-outfit text-foreground">Reglas de Puntos</CardTitle>
          <CardDescription className="text-muted-foreground">
            Configura cómo se acumulan y validan los puntos
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="text-foreground">Puntos por Visita</Label>
              <Input
                type="number"
                value={formData.points_per_visit}
                onChange={(e) => setFormData({ ...formData, points_per_visit: parseInt(e.target.value) || 0 })}
                className="bg-white/5 border-white/10 text-foreground"
                data-testid="points-per-visit-input"
              />
              <p className="text-xs text-muted-foreground">Puntos que gana el cliente por cada visita</p>
            </div>

            <div className="space-y-2">
              <Label className="text-foreground">Máximo Visitas/Día</Label>
              <Input
                type="number"
                value={formData.max_visits_per_day}
                onChange={(e) => setFormData({ ...formData, max_visits_per_day: parseInt(e.target.value) || 1 })}
                className="bg-white/5 border-white/10 text-foreground"
              />
              <p className="text-xs text-muted-foreground">Límite de visitas válidas por día</p>
            </div>

            <div className="space-y-2">
              <Label className="text-foreground">Cooldown (horas)</Label>
              <Input
                type="number"
                value={formData.visit_cooldown_hours}
                onChange={(e) => setFormData({ ...formData, visit_cooldown_hours: parseInt(e.target.value) || 1 })}
                className="bg-white/5 border-white/10 text-foreground"
              />
              <p className="text-xs text-muted-foreground">Tiempo mínimo entre visitas</p>
            </div>
          </div>

          <Button onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-500" data-testid="save-settings-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Guardar Configuración
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

// Main BackOffice Component
function BackOfficeDashboard() {
  const [activeTab, setActiveTab] = useState('dashboard');

  const renderTab = () => {
    switch (activeTab) {
      case 'dashboard': return <DashboardTab />;
      case 'branding': return <BrandingTab />;
      case 'levels': return <LevelsTab />;
      case 'rewards': return <RewardsTab />;
      case 'promotions': return <PromotionsTab />;
      case 'users': return <UsersTab />;
      case 'settings': return <SettingsTab />;
      default: return <DashboardTab />;
    }
  };

  return (
    <div className="flex h-screen bg-background">
      <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} />
      <main className="flex-1 overflow-y-auto p-8">
        {renderTab()}
      </main>
    </div>
  );
}

// BackOffice Router
export default function BackOffice() {
  const { currentStore, selectStore } = useStore();

  if (!currentStore) {
    return <BackOfficeSelector onSelect={selectStore} />;
  }

  return (
    <Routes>
      <Route path="/*" element={<BackOfficeDashboard />} />
    </Routes>
  );
}
